import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Modal, message, Icon, Spin, Tooltip } from "antd";
import { apiCall } from "./lib/frNetworkHelper";
import { verifyFrPassword } from "./lib/frSettingsHelper";
import Recognize from "./components/Recognize";
import Register from "./components/Register";
import AddPhoto from "./components/AddPhoto";
import dataUriToBlob from "./utils/dataUriToBlob";
import { useSelector } from "react-redux";

const FR_ENDPOINTS = {
  PERSONS: "persons",
  RECOGNIZE: "persons/recognize",
  PERSON_BY_ID: id => "persons/" + encodeURIComponent(id),
  PERSON_PHOTOS: id => "persons/" + encodeURIComponent(id) + "/photos",
  PERSON_SINGLE_PHOTO: (userId, fdId) =>
    "persons/" +
    encodeURIComponent(userId) +
    "/photos/" +
    encodeURIComponent(fdId),
};

const MODE = {
  LIST: "list",
  REGISTER: "register",
  EDIT: "edit",
  RECOGNIZE: "recognize"
};

const toErrorText = value => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (value && typeof value === "object") {
    if (typeof value.message === "string") return value.message;
    if (typeof value.error === "string") return value.error;
    if (value.error && typeof value.error.message === "string")
      return value.error.message;
  }
  return "";
};

const getErrorMessage = (err, fallback) =>
  toErrorText(err && err.data && err.data.error) ||
  toErrorText(err && err.data && err.data.message) ||
  fallback;

export default function FaceRecognition(props) {
  const authState = useSelector(state => state.authState);
  const userData = authState.userData;
  const BUSINESS_ID = userData.business_id;

  const [mode, setMode] = useState(); // list, recognize, add
  const [person, setPerson] = useState();
  const [photo, setPhoto] = useState();

  const [loading, setLoading] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordVerifying, setPasswordVerifying] = useState(false);
  const pendingProtectedActionRef = useRef(null);
  const photoEditFlowAuthorizedRef = useRef(false);

  const clearPhotoEditFlowAuth = () => {
    photoEditFlowAuthorizedRef.current = false;
  };

  const requirePhotoEditPassword = onSuccess => {
    if (!userData.fr_has_photo_edit_password) {
      onSuccess();
      return;
    }
    if (photoEditFlowAuthorizedRef.current) {
      onSuccess();
      return;
    }
    pendingProtectedActionRef.current = onSuccess;
    setPasswordModalVisible(true);
  };

  const handlePhotoPasswordConfirm = async () => {
    if (!passwordInput) {
      message.error("Password harus diisi");
      return;
    }
    setPasswordVerifying(true);
    const valid = await verifyFrPassword(
      userData.business_id,
      userData.outlet_id,
      "photo_edit",
      passwordInput
    );
    setPasswordVerifying(false);
    if (valid) {
      photoEditFlowAuthorizedRef.current = true;
      setPasswordModalVisible(false);
      setPasswordInput("");
      const action = pendingProtectedActionRef.current;
      pendingProtectedActionRef.current = null;
      if (action) {
        action();
      }
    } else {
      message.error("Password salah");
    }
  };

  const handlePhotoPasswordCancel = () => {
    setPasswordModalVisible(false);
    setPasswordInput("");
    pendingProtectedActionRef.current = null;
    clearPhotoEditFlowAuth();
    setMode("list");
    setPhoto(null);
  };

  const handleSetPhoto = value => {
    if (!value) {
      clearPhotoEditFlowAuth();
    }
    setPhoto(value);
  };

  const fetchPerson = personId => {
    apiCall(
      BUSINESS_ID,
      FR_ENDPOINTS.PERSON_BY_ID(personId),
      "get",
      {},
      resp => {
        const data = (resp && resp.data) || {};
        setPerson(data.person);
      },
      err => {
        if (err.status !== 404) {
          const msg = getErrorMessage(err, "Failed to fetch person");
          message.error(msg);
        }
      },
      () => setLoading(false)
    );
  };

  useEffect(() => {
    fetchPerson(props.user.user_id);
  }, [props.user.user_id]);

  // RECOGNIZE
  const [recognizeResult, setRecognizeResult] = useState(null);
  const recognizeFromDataUri = dataUri => {
    if (!dataUri) return;
    const blob = dataUriToBlob(dataUri);
    if (!blob) {
      message.error("Failed to read captured image");
      return;
    }

    const form = new FormData();
    form.append("image", blob, "photo.jpg");

    setLoading(true);

    apiCall(
      BUSINESS_ID,
      FR_ENDPOINTS.RECOGNIZE,
      "post",
      form,
      resp => {
        setRecognizeResult(resp.data);
      },
      err => {
        const msg = getErrorMessage(err, "Recognition failed");
        setRecognizeResult({ error: msg });
      },
      () => {
        setLoading(false);
      }
    );
  };

  // ADD PHOTO
  const addPhotoToPerson = photo => {
    const blob = dataUriToBlob(photo);
    if (!blob) {
      message.error("Failed to read captured image");
      return;
    }

    const form = new FormData();
    form.append("image", blob, "photo.jpg");

    setLoading(true);

    apiCall(
      BUSINESS_ID,
      FR_ENDPOINTS.PERSON_PHOTOS(props.user.user_id),
      "post",
      form,
      () => {
        message.success("Photo added");
        setPhoto(null);
        setMode("list");
        clearPhotoEditFlowAuth();
        fetchPerson(props.user.user_id);
      },
      err => {
        const msg = getErrorMessage(err, "Failed to add photo");
        message.error(msg);
      },
      () => setLoading(false)
    );
  };

  // DELETE FR DATA OF CURRENT PERSON
  const deletePerson = personId => {
    Modal.confirm({
      title: "Delete person?",
      content: "Are you sure you want to delete this person?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: function () {
        setLoading(true);

        apiCall(
          BUSINESS_ID,
          FR_ENDPOINTS.PERSON_BY_ID(personId),
          "delete",
          {},
          function () {
            message.success("Person deleted");
            setPerson(null);
          },
          function (err) {
            const msg = getErrorMessage(err, "Delete failed");
            message.error(msg);
          },
          () => setLoading(false)
        );
      }
    });
  };

  // DELETE PHOTO
  const deletePhoto = fd => {
    Modal.confirm({
      title: "Delete photo?",
      content: (
        <div>
          <p>Are you sure you want to delete this photo? </p>
          <img
            alt=""
            src={"data:image/jpeg;base64," + fd.imageData}
            style={{
              width: 100,
              height: 100,
              objectFit: "cover",
              borderRadius: 4,
              border: "1px solid #eee"
            }}
          />
        </div>
      ),
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        setLoading(true);

        apiCall(
          BUSINESS_ID,
          `persons/${props.user.user_id}/photos/${fd.id}`,
          "delete",
          {},
          () => {
            message.success("Photo deleted");
            fetchPerson(props.user.user_id);
          },
          err => {
            const msg = getErrorMessage(err, "Delete failed");
            message.error(msg);
          },
          () => setLoading(false)
        );
      }
    });
  };

  // REGISTER
  const register = () => {
    const blob = dataUriToBlob(photo);
    if (!blob) {
      message.error("Failed to read captured image");
      return;
    }

    const form = new FormData();
    form.append("image", blob, (blob && blob.name) || "photo.jpg");
    form.append("userId", props.user.user_id);
    form.append("name", props.user.user_name ?? "user#" + props.user.user_id);
    if ((props.email || "").trim())
      form.append("email", (props.email || "").trim());

    setLoading(true);

    apiCall(
      BUSINESS_ID,
      FR_ENDPOINTS.PERSONS,
      "post",
      form,
      () => {
        message.success("Person registered");
        setMode(MODE.LIST);
        fetchPerson(props.user.user_id);
        setPhoto(null);
        clearPhotoEditFlowAuth();
      },
      err => {
        const msg = getErrorMessage(err, "Delete failed");
        message.error(msg);
        setPhoto(null);
        clearPhotoEditFlowAuth();
      },
      () => setLoading(false)
    );
  };

  const isMaxPhotosReached = (person?.faceDescriptors || []).length >= 3;

  return (
    <>
      {person ? (
        <Spin spinning={loading}>
          <p>
            {person.name}
          </p>
          {person.email ? <p>{person.email}</p> : null}
          {person.role ? <p>{person.role}</p> : null}

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 12
            }}
          >
            {(person.faceDescriptors || []).slice(0, 6).map(fd => (
              <div key={fd.id} style={{ position: "relative", display: "inline-block" }}>
                <img
                  alt="face"
                  src={"data:image/jpeg;base64," + fd.imageData}
                  style={{
                    width: 200,
                    height: 200,
                    objectFit: "cover",
                    borderRadius: 4,
                    border: "1px solid #eee"
                  }}
                />
                {person.faceDescriptors.length > 1 && (
                  <Icon
                    type="delete"
                    onClick={() => deletePhoto(fd)}
                    style={{
                      position: "absolute",
                      bottom: 8,
                      right: 8,
                      fontSize: 18,
                      color: "#fff",
                      backgroundColor: "rgba(0, 0, 0, 0.45)",
                      borderRadius: "50%",
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer"
                    }}
                  />
                )}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: "5px"}}>
            <Button onClick={() => setMode("recognize")}>Test Recognition</Button>
            <Tooltip
              title={isMaxPhotosReached ? "Maximum 3 photos per person" : ""}
            >
              <span>
                <Button
                  onClick={() => requirePhotoEditPassword(() => setMode("add"))}
                  disabled={isMaxPhotosReached}
                >
                  Add More Photo
                </Button>
              </span>
            </Tooltip>
            <Button type="danger" onClick={() => deletePerson(person.id)}>
              Delete
            </Button>
          </div>

          <Recognize
            loading={loading}
            photo={photo}
            visible={mode === "recognize"}
            recognizeFromDataUri={recognizeFromDataUri}
            recognizeResult={recognizeResult}
            onClose={() => {
              setMode("list");
              setPhoto(null);
              setRecognizeResult(null);
            }}
          />

          <AddPhoto
            loading={loading}
            photo={photo}
            setPhoto={handleSetPhoto}
            visible={mode === "add"}
            addPhotoToPerson={addPhotoToPerson}
            onClose={() => {
              setMode("list");
              setPhoto(null);
              clearPhotoEditFlowAuth();
            }}
          />
        </Spin>
      ) : (
        <Register
          loading={loading}
          photo={photo}
          setPhoto={handleSetPhoto}
          register={() => requirePhotoEditPassword(register)}
          requirePhotoEditPassword={requirePhotoEditPassword}
        />
      )}

      <Modal
        title="Akses Edit Foto"
        visible={passwordModalVisible}
        onOk={handlePhotoPasswordConfirm}
        confirmLoading={passwordVerifying}
        onCancel={handlePhotoPasswordCancel}
        okText="Konfirmasi"
      >
        <Input.Password
          value={passwordInput}
          onChange={e => setPasswordInput(e.target.value)}
          onPressEnter={handlePhotoPasswordConfirm}
          placeholder="Masukkan password edit foto"
          disabled={passwordVerifying}
        />
      </Modal>
    </>
  );
}
